// @ts-nocheck
/**
 * ApiKeysSection: the keys another product uses to call the API from its
 * server (2026-10-26). An admin creates a key for a member; the key acts as
 * that member. It is shown once, at creation: only its hash is kept.
 *
 * W4 (B2-settings-apiKeys, DX-1):
 *   - links to the guide (/developers) and the OpenAPI reference (/api/docs);
 *   - the keys in a table with « Dernière utilisation » (last_used_at);
 *   - after creation: the key once, the base URL, a ready-to-copy curl for
 *     the first page of contacts (the key exported on its own line, then
 *     read as $XDIAL_API_KEY, like /developers, QA-W7-14), and « Tester », which
 *     makes that call with the key alone and shows the JSON or the error code;
 *   - revoking is destructive: ConfirmDialog (DES-13).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { BookOpen, Copy, ExternalLink, Loader2, Play, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/api/api-client';
import { listMembers } from '@/api/endpoints/admin';
import { errorMessage, errorToastOptions } from '@/services/errorMessage';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuthStore } from '@/stores/authStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DataTable } from '@/components/shared/DataTable';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { CodeBlock } from '@/components/shared/CodeBlock';
import { copyText } from '@/components/shared/CopyLink';
import { apiBaseUrl, curlPage, exportEnv, testApiKey } from '@/lib/apiExamples';
import { SettingsCard } from './SettingsPrimitives';

const day = (iso, lang) => (iso ? new Date(iso).toLocaleDateString(lang) : null);

export const DEVELOPERS_PATH = '/developers';
export const OPENAPI_DOCS_URL = '/api/docs';

export function ApiKeysSection() {
  const { t, i18n } = useTranslation(['settings', 'common']);
  const { isTenantAdmin } = usePermissions();
  const queryClient = useQueryClient();
  const organisationId = useAuthStore((s) => s.activeMembership?.organisationId);
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState(null);

  const keysQuery = useQuery({
    queryKey: ['api-keys'],
    queryFn: async () => (await api.functions.invoke('listApiKeys', {})).data?.keys || [],
    enabled: isTenantAdmin,
  });
  const keys = keysQuery.data ?? [];

  if (!isTenantAdmin) {
    return <p className="text-sm text-muted-foreground">{t('settings:apiKeys.askAdmin')}</p>;
  }

  const revoke = async () => {
    try {
      await api.functions.invoke('revokeApiKey', { id: revoking.id });
      toast.success(t('settings:apiKeys.revoked'));
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      setRevoking(null);
    } catch (err) {
      toast.error(errorMessage(err, t, 'settings:apiKeys.revokeFailed'), errorToastOptions(err));
      throw err;
    }
  };

  const columns = [
    { id: 'name', header: t('settings:apiKeys.columns.name'), cell: (k) => <span className="font-medium text-foreground">{k.name}</span> },
    { id: 'prefix', header: t('settings:apiKeys.columns.prefix'), cell: (k) => <code className="text-xs">{k.display_prefix}…</code> },
    {
      id: 'member',
      header: t('settings:apiKeys.columns.member'),
      cell: (k) => <span className="text-xs">{k.member_email || '?'} ({k.member_role ? t(`settings:apiKeys.roles.${k.member_role}`) : '?'})</span>,
    },
    { id: 'created', header: t('settings:apiKeys.columns.created'), cell: (k) => day(k.created_date, i18n.language), hideable: true },
    {
      id: 'lastUsed',
      header: t('settings:apiKeys.columns.lastUsed'),
      cell: (k) => (k.last_used_at ? day(k.last_used_at, i18n.language) : <span className="text-muted-foreground">{t('settings:apiKeys.neverUsed')}</span>),
    },
    {
      id: 'actions',
      header: <span className="sr-only">{t('settings:apiKeys.columns.actions')}</span>,
      align: 'right',
      cell: (k) => (k.revoked_at ? (
        <span className="text-xs text-muted-foreground">{t('settings:apiKeys.revokedOn', { date: day(k.revoked_at, i18n.language) })}</span>
      ) : (
        <Button variant="ghost" size="sm" className="h-11 text-xs" aria-label={t('settings:apiKeys.revokeAria', { name: k.name })} onClick={() => setRevoking(k)}>
          {t('settings:apiKeys.revoke')}
        </Button>
      )),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <Link to={DEVELOPERS_PATH} className="inline-flex min-h-11 items-center gap-1.5 font-medium" style={{ color: 'var(--vf-accent-text)' }}>
            <BookOpen className="h-4 w-4" aria-hidden />
            {t('settings:apiKeys.guideLink')}
          </Link>
          <a href={OPENAPI_DOCS_URL} target="_blank" rel="noreferrer" className="inline-flex min-h-11 items-center gap-1.5 font-medium" style={{ color: 'var(--vf-accent-text)' }}>
            <ExternalLink className="h-4 w-4" aria-hidden />
            {t('settings:apiKeys.openapiLink')}
          </a>
        </div>
        <Button size="sm" className="h-9" onClick={() => setCreating(true)}>
          <Plus className="w-3.5 h-3.5 mr-1.5" aria-hidden />
          {t('settings:apiKeys.create')}
        </Button>
      </div>

      <SettingsCard>
        <div className="p-3">
          <DataTable
            caption={t('settings:apiKeys.caption')}
            columns={columns}
            rows={keys}
            getRowId={(k) => k.id}
            status={keysQuery.isLoading ? 'loading' : keysQuery.isError ? 'error' : 'ready'}
            error={keysQuery.isError ? { message: t('settings:apiKeys.loadFailed'), onRetry: () => void keysQuery.refetch() } : undefined}
            empty={{
              title: t('settings:apiKeys.empty'),
              action: <Button size="sm" onClick={() => setCreating(true)}>{t('settings:apiKeys.create')}</Button>,
            }}
            storageKey="settings-api-keys"
          />
        </div>
      </SettingsCard>

      <CreateApiKeyDialog open={creating} onOpenChange={setCreating} organisationId={organisationId} />

      <ConfirmDialog
        open={!!revoking}
        onOpenChange={(v) => { if (!v) setRevoking(null); }}
        title={t('settings:apiKeys.revokeTitle', { name: revoking?.name })}
        description={t('settings:apiKeys.revokeWhy')}
        confirmLabel={t('settings:apiKeys.revokeConfirm')}
        onConfirm={revoke}
      />
    </div>
  );
}

/** « Tester »: the first page of contacts with the key alone; the JSON, or the error code. */
function ApiKeyTester({ base, secret }) {
  const { t } = useTranslation(['settings']);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    setRunning(true);
    setResult(await testApiKey(base, secret));
    setRunning(false);
  };
  return (
    <div className="space-y-2">
      <Button variant="outline" size="sm" className="h-9" disabled={running} onClick={() => void run()}>
        {running ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" aria-hidden /> : <Play className="w-3.5 h-3.5 mr-1.5" aria-hidden />}
        {t('settings:apiKeys.test')}
      </Button>
      <div aria-live="polite" data-testid="api-key-test-result">
        {result?.ok && (
          <>
            <p className="text-xs" style={{ color: 'var(--vf-status-connected-text, inherit)' }}>{t('settings:apiKeys.testOk', { status: result.status })}</p>
            <pre className="mt-1 max-h-48 overflow-auto rounded-md border bg-muted px-3 py-2 text-[11px]">{result.json.length > 4000 ? `${result.json.slice(0, 4000)}\n…` : result.json}</pre>
          </>
        )}
        {result && !result.ok && (
          <>
            <p className="text-xs text-destructive">{t(result.status ? 'settings:apiKeys.testFailed' : 'settings:apiKeys.testNetwork', { status: result.status })}</p>
            <p className="font-mono text-[11px] text-muted-foreground">{result.code}</p>
          </>
        )}
      </div>
    </div>
  );
}

/** Names the key and the member it acts as, then shows the key once with a ready-to-run example (DX-1). */
export function CreateApiKeyDialog({ open, onOpenChange, organisationId, baseUrl }) {
  const { t } = useTranslation(['settings', 'common']);
  const queryClient = useQueryClient();
  const myMembership = useAuthStore((s) => s.activeMembership?.membershipId);
  const [name, setName] = useState('');
  const [memberId, setMemberId] = useState('');
  const [secret, setSecret] = useState('');
  const [busy, setBusy] = useState(false);
  const base = baseUrl ?? apiBaseUrl();

  const { data: members = [] } = useQuery({
    queryKey: ['org-members', organisationId],
    queryFn: ({ signal }) => listMembers(organisationId, { signal }),
    enabled: open && !!organisationId,
  });

  const close = () => {
    if (busy) return;
    setName('');
    setMemberId('');
    setSecret('');
    onOpenChange(false);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.functions.invoke('createApiKey', { name: name.trim(), membershipId: memberId || myMembership });
      setSecret(data?.key || '');
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
    } catch (err) {
      toast.error(errorMessage(err, t, 'settings:apiKeys.createFailed'), errorToastOptions(err));
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (await copyText(secret)) toast.success(t('settings:apiKeys.copied'));
    else toast.error(t('settings:apiKeys.copyFailed'));
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className={secret ? 'max-w-2xl' : 'max-w-md'}>
        <DialogHeader>
          <DialogTitle className="text-foreground">{t(secret ? 'settings:apiKeys.createdTitle' : 'settings:apiKeys.createTitle')}</DialogTitle>
          <DialogDescription>{t(secret ? 'settings:apiKeys.createdWhy' : 'settings:apiKeys.createWhy')}</DialogDescription>
        </DialogHeader>

        {secret ? (
          <div className="min-w-0 space-y-4">
            <div className="flex items-center gap-2">
              <code className="flex-1 min-w-0 break-all text-xs bg-muted rounded px-2 py-2 select-all" data-testid="api-key-secret">{secret}</code>
              <Button variant="outline" size="sm" className="h-9 shrink-0" onClick={() => void copy()}>
                <Copy className="w-3.5 h-3.5 mr-1.5" aria-hidden />{t('settings:apiKeys.copy')}
              </Button>
            </div>
            <div>
              <p className="text-xs font-medium text-foreground">{t('settings:apiKeys.baseUrl')}</p>
              <code className="mt-1 block break-all text-xs" data-testid="api-key-base-url">{base}</code>
            </div>
            <div className="space-y-1">
              <p className="text-xs font-medium text-foreground">{t('settings:apiKeys.tryIt')}</p>
              {/* QA-W7-14: like /developers, the key goes in the environment
                  on its own line; the curl reads $XDIAL_API_KEY. */}
              <CodeBlock code={`${exportEnv(base, secret)}\n\n${curlPage()}`} label="curl" testId="api-key-curl" />
            </div>
            <ApiKeyTester base={base} secret={secret} />
            {/* REV-W4-F8: in this tab the link unmounted the dialog and the
                key, shown once, was lost. A new tab keeps it on screen. */}
            <Link to={DEVELOPERS_PATH} target="_blank" rel="noreferrer" className="inline-flex min-h-11 items-center gap-1.5 text-sm" style={{ color: 'var(--vf-accent-text)' }}>
              <BookOpen className="h-4 w-4" aria-hidden />
              {t('settings:apiKeys.guideNext')}
              <span className="sr-only">{t('settings:apiKeys.guideNextNewTab')}</span>
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label htmlFor="api-key-name" className="text-xs">{t('settings:apiKeys.name')}</Label>
              <Input id="api-key-name" className="h-9 mt-1" value={name} maxLength={80} placeholder={t('settings:apiKeys.namePlaceholder')} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">{t('settings:apiKeys.member')}</Label>
              <Select value={memberId || myMembership || ''} onValueChange={setMemberId}>
                <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {members.map((m) => (
                    <SelectItem key={m.membership_id} value={m.membership_id}>
                      {m.full_name || m.email} ({t(`settings:apiKeys.roles.${m.role}`)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">{t('settings:apiKeys.memberHint')}</p>
            </div>
          </div>
        )}

        <DialogFooter>
          {secret ? (
            <Button size="sm" className="h-9" onClick={close}>{t('settings:apiKeys.done')}</Button>
          ) : (
            <>
              <Button variant="ghost" size="sm" className="h-9" disabled={busy} onClick={close}>{t('common:actions.cancel')}</Button>
              <Button size="sm" className="h-9" disabled={!name.trim() || busy} onClick={() => void submit()}>
                {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" aria-hidden /> : null}
                {t('settings:apiKeys.submit')}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
